/**
 * Checkout entry model (booking-detail-reskin, design D3).
 *
 * The `/payment` page and its checkout chain support two entry shapes, mirroring
 * the server's one-route-two-shapes contract (`/payment/intent`, `/payment/status`,
 * `/payment/manual-confirm` each accept `flowToken` OR `bookingId`):
 *
 * - `flow`        — the F9 flow-token entry (active booking flow, hold clock).
 * - `postApproval` — a PENDING_PAYMENT booking approved by an admin (no flow
 *                    context, no hold-expiry timer); entered by `?bookingId=`.
 *
 * studio-shop D6: the card panel also pays for a shop order (`shopOrder`), through
 * the shop's own intent route; the booking routes never see it.
 *
 * Idempotency-Key / carrier / poll-query identity is scoped by
 * {@link checkoutEntryIdentity} so a flow attempt and a postApproval attempt
 * never collide.
 */
export type CheckoutEntry =
  | { kind: 'flow'; flowToken: string; recipientToken?: string }
  | { kind: 'postApproval'; bookingId: string }
  | { kind: 'shopOrder'; orderId: string };

/**
 * Stable identity string for an entry — used to scope query keys, carrier
 * localStorage keys, and Idempotency-Key derivation. `recipientToken` is NOT
 * part of the identity (it is attribution metadata, not the payment subject).
 */
export function checkoutEntryIdentity(entry: CheckoutEntry): string {
  if (entry.kind === 'shopOrder') return `shop:${entry.orderId}`;
  return entry.kind === 'flow' ? `flow:${entry.flowToken}` : `booking:${entry.bookingId}`;
}

/** The booking payment routes' subject: a flow's token or an approved booking's id. A shop order has its own routes. */
export function bookingPaymentSubject(entry: CheckoutEntry): { flowToken: string } | { bookingId: string } {
  if (entry.kind === 'shopOrder') throw new Error('A shop order is paid through the shop routes.');
  return entry.kind === 'flow' ? { flowToken: entry.flowToken } : { bookingId: entry.bookingId };
}
